import { alpha } from "@mui/material"
import fp from "lodash/fp"

import type {
  SpanPlotDef,
  LinePlotView,
  SingleStatPlotView,
  TableStatPlotView,
} from "types/dashboard.types"

import { darkTheme } from "styles/themes"
import { lineView, singleStatView } from "helpers/config/dashboards/base/views"
import {
  linePlotDefBase,
  singleStatPlotDefBase,
  tableStatPlotDefBase,
} from "helpers/config/dashboards/base/defs"
import { getFieldFormat } from "helpers/utils/telemetryFields"
import {
  SPAN_TEMPERATURE_FIELD,
  SPAN_AMPACITY_AAR_FIELD,
  SPAN_AMPACITY_DLR_FIELD,
  SPAN_AMPACITY_FORECAST_AAR_FIELD,
  SPAN_AMPACITY_FORECAST_DLR_FIELD,
} from "helpers/config/telemetryFieldDefs"

// ─── Ampacity ─────────────────────────────────────────────────────────────────

const LONG_TERM_AMPACITY_FIELDS = [
  SPAN_AMPACITY_AAR_FIELD,
  SPAN_AMPACITY_DLR_FIELD,
  SPAN_AMPACITY_FORECAST_AAR_FIELD,
  SPAN_AMPACITY_FORECAST_DLR_FIELD,
]

// Mirrors the powerline's long-term ampacity view, series for series, so the two
// pages read the same way. No "span" in the title, the page already says which
// span this is.
const longTermAmpacityPlotView: LinePlotView = fp.merge(lineView, {
  name: "Long-term ampacity",
  nameKey: "SPAN_LONG_TERM_AMPACITY",
  helpTooltip: [
    "plots_descriptions.SPAN_LONG_TERM_AMPACITY",
    "plots_descriptions.AMPACITY_AAR",
    "plots_descriptions.AMPACITY_DLR",
  ],
  properties: {
    axes: {
      y: getFieldFormat(LONG_TERM_AMPACITY_FIELDS),
    },
    fieldProperties: {
      [`{"_field":"${SPAN_AMPACITY_AAR_FIELD}"}`]: {
        color: darkTheme.palette.primary.main,
      },
      [`{"_field":"${SPAN_AMPACITY_DLR_FIELD}"}`]: {
        color: darkTheme.palette.secondary.main,
      },
      [`{"_field":"${SPAN_AMPACITY_FORECAST_AAR_FIELD}"}`]: {
        color: darkTheme.palette.extras.lavender.main,
        backgroundShading: true,
      },
      [`{"_field":"${SPAN_AMPACITY_FORECAST_DLR_FIELD}"}`]: {
        color: darkTheme.palette.extras.limeGreen.main,
        backgroundShading: true,
      },
    },
    lineFills: [
      {
        lineA: SPAN_AMPACITY_FORECAST_DLR_FIELD,
        lineB: SPAN_AMPACITY_FORECAST_AAR_FIELD,
        color: alpha(darkTheme.palette.extras.limeGreen.main, 0.15),
      },
    ],
  },
})

export const spanLongTermAmpacityPlotDef: SpanPlotDef = fp.merge(linePlotDefBase, {
  view: longTermAmpacityPlotView,
  dataSource: { entity: "span", resolver: "self", service: "telemetry" },
  reqConfig: {
    fields: LONG_TERM_AMPACITY_FIELDS,
    forecastFields: [SPAN_AMPACITY_FORECAST_AAR_FIELD, SPAN_AMPACITY_FORECAST_DLR_FIELD],
    aggregate: ["mean"],
    group: ["span_id", "_field"],
    measurement: "dlr",
  },
})

// ─── Last ampacity ────────────────────────────────────────────────────────────

const lastAmpacityAARPlotView: SingleStatPlotView = fp.merge(singleStatView, {
  name: "Last ampacity (AAR)",
  nameKey: "SPAN_LAST_AMPACITY_AAR",
  properties: {
    footerFields: ["_time"],
    ...getFieldFormat([SPAN_AMPACITY_AAR_FIELD]),
    size: "lg",
    formatUnits: true,
    decimalPlaces: 0,
  },
})

export const spanLastAmpacityAARPlotDef: SpanPlotDef = fp.merge(singleStatPlotDefBase, {
  view: lastAmpacityAARPlotView,
  dataSource: { entity: "span", resolver: "self", service: "telemetry" },
  reqConfig: {
    fields: [SPAN_AMPACITY_AAR_FIELD],
    method: ["last"],
    group: ["span_id", "_field"],
    measurement: "dlr",
  },
})

const lastAmpacityDLRPlotView: SingleStatPlotView = fp.merge(singleStatView, {
  name: "Last ampacity (DLR)",
  nameKey: "SPAN_LAST_AMPACITY_DLR",
  properties: {
    footerFields: ["_time"],
    ...getFieldFormat([SPAN_AMPACITY_DLR_FIELD]),
    size: "lg",
    formatUnits: true,
    decimalPlaces: 0,
  },
})

export const spanLastAmpacityDLRPlotDef: SpanPlotDef = fp.merge(singleStatPlotDefBase, {
  view: lastAmpacityDLRPlotView,
  dataSource: { entity: "span", resolver: "self", service: "telemetry" },
  reqConfig: {
    fields: [SPAN_AMPACITY_DLR_FIELD],
    method: ["last"],
    group: ["span_id", "_field"],
    measurement: "dlr",
  },
})

// Same series as the long-term ampacity plot, so the percentage matches what it
// shows. The threshold comes from the span, through useSpanPlotView.
const belowNominalAmpacityTableView: TableStatPlotView = {
  name: "% Below nominal ampacity",
  nameKey: "SPAN_BELOW_NOMINAL_AMPACITY",
  type: "table-stat",
  properties: {
    columns: [
      { key: "method", labelKey: "table_stats.METHOD", type: "label" },
      { key: "percentage", labelKey: "table_stats.PERCENTAGE", type: "percentageBar" },
    ],
    rows: [
      {
        field: SPAN_AMPACITY_DLR_FIELD,
        labelKey: "table_stats.DLR_FULL",
        color: darkTheme.palette.secondary.main,
      },
      {
        field: SPAN_AMPACITY_AAR_FIELD,
        labelKey: "table_stats.AAR_FULL",
        color: darkTheme.palette.primary.main,
      },
    ],
  },
}

export const spanBelowNominalAmpacityTablePlotDef: SpanPlotDef = fp.merge(
  tableStatPlotDefBase,
  {
    view: belowNominalAmpacityTableView,
    dependencies: [longTermAmpacityPlotView.nameKey],
    downloadable: false,
    dataSource: { entity: "span", resolver: "self", service: "telemetry" },
    reqConfig: {
      fields: [SPAN_AMPACITY_AAR_FIELD, SPAN_AMPACITY_DLR_FIELD],
      aggregate: ["mean"],
      group: ["span_id", "_field"],
      measurement: "dlr",
      fetchQualitySummary: false,
    },
  },
)

// ─── Conductor temperature ────────────────────────────────────────────────────

const conductorTemperaturePlotView: LinePlotView = fp.merge(lineView, {
  name: "Conductor temperature (DLR estimate)",
  nameKey: "SPAN_CONDUCTOR_TEMPERATURE",
  // The limit lines are drawn from the span, so the tooltip names and explains
  // them with the same wording the settings tab uses for those two fields.
  helpTooltip: [
    "plots_descriptions.SPAN_CONDUCTOR_TEMPERATURE",
    "plots_descriptions.SPAN_TEMPERATURE_MAX",
    "plots_descriptions.SPAN_EMERGENCY_TEMPERATURE_MAX",
  ],
  properties: {
    axes: {
      y: getFieldFormat([SPAN_TEMPERATURE_FIELD]),
    },
  },
})

export const spanConductorTemperaturePlotDef: SpanPlotDef = fp.merge(linePlotDefBase, {
  view: conductorTemperaturePlotView,
  dataSource: { entity: "span", resolver: "self", service: "telemetry" },
  reqConfig: {
    fields: [SPAN_TEMPERATURE_FIELD],
    aggregate: ["mean"],
    group: ["span_id", "_field"],
    measurement: "dlr",
  },
})

const lastConductorTemperaturePlotView: SingleStatPlotView = fp.merge(singleStatView, {
  name: "Last conductor temperature (DLR estimate)",
  nameKey: "SPAN_LAST_CONDUCTOR_TEMPERATURE",
  properties: {
    footerFields: ["_time"],
    ...getFieldFormat([SPAN_TEMPERATURE_FIELD]),
    size: "lg",
  },
})

export const spanLastConductorTemperaturePlotDef: SpanPlotDef = fp.merge(
  singleStatPlotDefBase,
  {
    view: lastConductorTemperaturePlotView,
    dataSource: { entity: "span", resolver: "self", service: "telemetry" },
    reqConfig: {
      fields: [SPAN_TEMPERATURE_FIELD],
      method: ["last"],
      group: ["span_id", "_field"],
      measurement: "dlr",
    },
  },
)
